import {useMutation} from '@apollo/client'
import {AutoSave} from '@flinkit/ra-form-layout'
import {
  ReferenceManyToManyField,
  ReferenceManyToManyInput,
} from '@flinkit/ra-relationships'
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  InputAdornment,
  Paper,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import {makeStyles} from '@mui/styles'
import gql from 'graphql-tag'
import {sortBy} from 'lodash-es'
import {FC, useCallback, useState} from 'react'
import {
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanInput,
  ChipField,
  Create,
  Datagrid,
  EditButton,
  FilterLiveSearch,
  FunctionField,
  Identifier,
  Pagination,
  RadioButtonGroupInput,
  ReferenceInput,
  regex,
  required,
  SaveButton,
  SaveContext,
  SelectInput,
  SimpleForm,
  SingleFieldList,
  TextInput,
  Toolbar,
  useCreate,
  useEditContext,
  useEditController,
  useGetList,
  useListContext,
  useLocale,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin'
import {FieldValues, useFormContext, useWatch} from 'react-hook-form'

import ExtendedEdit from '../components/ExtendedEdit'
import {ChevronRightIcon, SearchIcon} from '../components/icons'
import MultipleChipSelect from '../components/MultipleChipSelect'
import useHasPermission from '../hooks/useHasPermission'
import useIsDesktop from '../hooks/useIsDesktop'
import useSessionMerchantUser from '../hooks/useSessionMerchantUser'
import useSoftDelete from '../hooks/useSoftDelete'
import {CompanySettingsTab} from '../pages/CompanySettingsPage'
import {
  ChannelAccordionsList,
} from '../pages/CompanySettingsPage/ChannelsAndMerchantUsersManagementShow'
import {
  ChannelMerchantUsers,
  Channels,
  ContactGroups,
  MerchantUserContactGroups,
  MerchantUsers,
} from '../types/graphqlSchema'
import {EMAIL_ADDRESS_REGEX} from '../utils/consts'
import {actionErrorCode} from '../utils/errors'
import {SUPPORTED_I18N_LANGUAGES} from '../utils/i18nProvider'
import DeleteWithConfirmButton from './components/DeleteWithConfirmButton'
import ExtendedList from './components/ExtendedList'
import {ShowHeader} from './components/ResourceHeader'

const MerchantUserList = () => {
  const translate = useTranslate()
  return (
    <ExtendedList
      filters={[
        <FilterLiveSearch
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon color="secondary" />
                </InputAdornment>
              ),
              onKeyDown: e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  e.stopPropagation()
                }
              },
            },
          }}
          alwaysOn
          key="freeTextSearch"
          source="firstName@_ilike,lastName@_ilike,emailAddress@_ilike"
        />,
        <ReferenceInput
          key="channelMerchantUsers#channelId"
          label="Channels"
          source="channelMerchantUsers#channelId"
          reference="channels"
        >
          <AutocompleteInput
            filterToQuery={q => ({'name@_ilike': q})}
            label="Channel name"
          />
        </ReferenceInput>,
        <ReferenceInput
          key="merchantUserContactGroups#contactGroupId"
          label="Contact visibility"
          source="merchantUserContactGroups#contactGroupId"
          reference="contact_groups"
        >
          <AutocompleteInput
            filterToQuery={q => ({'name@_ilike': q})}
            label="Group name"
          />
        </ReferenceInput>,
        <SelectInput
          choices={[
            {name: translate('ra.boolean.true'), value: 'true'},
            {name: translate('ra.boolean.false'), value: 'false'},
          ]}
          key="isAdmin"
          label={translate('resources.merchant_users.fields.isAdmin')}
          isRequired
          optionText="name"
          optionValue="value"
          source="isAdmin"
          sx={{'& span[aria-hidden="true"]': {display: 'none'}}}
        />,
        <SelectInput
          choices={[
            {name: translate('ra.boolean.true'), value: 'true'},
            {name: translate('ra.boolean.false'), value: 'false'},
          ]}
          key="isOwner"
          label={translate('resources.merchant_users.fields.isOwner')}
          isRequired
          optionText="name"
          optionValue="value"
          source="isOwner"
          sx={{'& span[aria-hidden="true"]': {display: 'none'}}}
        />,
      ]}
      pagination={<Pagination rowsPerPageOptions={[]} />}
      perPage={25}
      resource="merchant_users"
      title={false}
    >
      <MerchantUserDatagrid />
    </ExtendedList>
  )
}

const MerchantUserDatagrid = () => {
  const styles = useStyles()
  return (
    <Datagrid bulkActionButtons={false} rowClick={false}>
      <FunctionField<MerchantUsers>
        label={useTranslate()('resources.merchant_users.fields.name')}
        render={u => [u?.firstName, u?.lastName].join(' ')}
        source="firstName"
      />
      <ReferenceManyToManyField
        joinLimit={4}
        label="Channels"
        reference="channels"
        sort={{field: "channelId", order: 'DESC'}}
        through="channel_merchant_users"
        using="merchantUserId,channelId"
      >
        <SingleFieldList linkType={false}><ChannelChipField /></SingleFieldList>
      </ReferenceManyToManyField>
      <ReferenceManyToManyField
        reference="contact_groups"
        through="merchant_user_contact_groups"
        using="merchantUserId,contactGroupId"
        sort={{field: "channelId", order: 'DESC'}}
        label="Contacts visibility"
      >
        <SingleFieldList linkType={false}>
          <ChipField className={styles.listDatagridContactGroupChipField} source="name" />
        </SingleFieldList>
      </ReferenceManyToManyField>
      <EditButton
        icon={<ChevronRightIcon color="info" />}
        label=""
        // @ts-ignore
        source="editIconButton"
      />
    </Datagrid>
  )
}

const ChannelChipField = () => {
  const styles = useStyles()
  const record = useRecordContext<Channels>()
  const {data} = useListContext<Channels>()
  const index = data?.findIndex(r => r.id === record?.id)
  if (index === 3) return (
    <Chip label=">..." />
  )
  return (
    <ChipField
      className={styles.listDatagridChannelChipField}
      source="name"
    />
  )
}

const MerchantUserCreate = () => {
  const styles = useStyles()
  const {merchantUser} = useSessionMerchantUser()
  const translate = useTranslate()
  const [isLimitedToContactGroups, setIsLimitedToSpecificContactGroups] = useState(false)
  const {data: contactGroups = []} = useGetList('contact_groups')
  return (
    <Create
      className={styles.merchantUserCreateRoot}
      redirect={false}
      resource="merchant_users"
    >
      <SimpleForm
        defaultValues={{merchantId: merchantUser?.merchant?.id}}
        toolbar={<MerchantUserSaveOrRestoreButton />}
      >
        <TextInput source="firstName" validate={required()}/>
        <TextInput source="lastName" validate={required()} />
        <TextInput
          parse={v => v?.toLowerCase()}
          source="emailAddress"
          validate={[regex(
            EMAIL_ADDRESS_REGEX,
            translate('resources.merchant_users.errorMessages.invalidEmailAddress')
          ), required()]}
        />
        <div className={styles.contactsVisibilityContainer}>
          <Typography variant="subtitle2">{translate(
            'pages.companySettings.tabs.addNumbersAndMerchantUsers.contactsVisibility.' +
            'title'
          )}</Typography>
        </div>
        <FormControlLabel
          control={(
            <Checkbox
              checked={isLimitedToContactGroups}
              disabled={!contactGroups.length}
              onChange={
                event => setIsLimitedToSpecificContactGroups(event.target.checked)
              }
            />
          )}
          label={translate(
            'pages.companySettings.tabs.addNumbersAndMerchantUsers.createMerchantUser.' +
            'restrictContactGroups'
          )}
        />
        {isLimitedToContactGroups && (
          <ReferenceManyToManyInput
            perPage={-1}
            perPageChoices={-1}
            reference="contact_groups"
            sortChoices={{field: 'name', order: 'ASC'}}
            through="merchant_user_contact_groups"
            using="merchantUserId,contactGroupId"
          >
            <AutocompleteArrayInput
              filterToQuery={q => ({'name@_ilike': q})}
              label="resources.contactGroups.name"
              optionText="name"
              validate={required()}
            />
          </ReferenceManyToManyInput>
        )}
      </SimpleForm>
    </Create>
  )
}

const MerchantUserSaveOrRestoreButton = ({...props}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const {reset} = useFormContext()
  const {emailAddress, firstName, lastName} = useWatch()
  const [restoreMerchantUser] = useMutation(
    RESTORE_DELETED_MERCHANT_USER_MUTATION, {
      onCompleted: ({restoreMerchantUser: {isRestored}}) => {
        if (isRestored) {
          notify('resources.merchant_users.successMessages.created')
          reset()
          refresh()
        }
        else {
          notify(
            'resources.merchant_users.errorMessages.' +
            'MERCHANT_USER_EMAIL_ADDRESS_ALREADY_EXISTS',
            {type: 'error'},
          )
        }
      },
      variables: {emailAddress, firstName, lastName},
    }
  )
  return (
    <SaveButton
      label={translate('resources.merchant_users.actions.create')}
      mutationOptions={{
        onError: e => {
          if ((e as Error).message.endsWith(
            'Uniqueness violation. duplicate key value violates ' +
            'unique constraint "merchant_users_email_address_key"'
          )) {
            restoreMerchantUser()
          }
        },
        onSuccess: () => {
          notify('resources.merchant_users.successMessages.created')
          reset()
          refresh()
        },
      }}
      type="button"
      {...props}
    />
  )
}

const MerchantUserEdit = () => {
  const styles = useStyles()
  const translate = useTranslate()
  const isDesktop = useIsDesktop()
  const editController = useEditController()
  const {data: [user] = []} = useGetList(
    'users',
    {filter: {merchantUserId: editController.record?.id}},
    {enabled: !!editController.record?.id}
  )
  const softDelete = useSoftDelete({
    ids: [user?.id],
    onError: console.error,
    redirectTo: `/company-settings#${CompanySettingsTab.MerchantUsers}`,
    resource: 'users',
  })
  return (
    <ExtendedEdit
      actions={
        <ShowHeader title={translate('resources.merchant_users.edit.title')} />
      }
      aside={isDesktop ? <MerchantUserEditAside /> : undefined}
      redirect={false}
      resource="merchant_users"
      title={translate('resources.merchant_users.edit.title')}
    >
      <SimpleForm
        className={styles.merchantUserEditRoot}
        resetOptions={{keepDirtyValues: true}}
        toolbar={(
          <AutoSave
            confirmationDuration={false}
            debounce={1000}
            typographyProps={{display: 'none'}}
          />
        )}
      >
        <TextInput source="firstName" validate={required()}/>
        <TextInput source="lastName" />
        <MerchantUserEmailEdit />
        <br/><br/>
        <Typography className={styles.featureSettingsTitle} variant="subtitle2">
          {translate('resources.merchant_users.edit.featureSettingsTitle')}
        </Typography>
        <BooleanInput className={styles.editIsOwnerInput} source="isOwner" />
        <ReferenceManyToManyField
          reference="channels"
          sort={{field: "channel.whatsappAccount.phoneNumber", order: "DESC"}}
          through="channel_merchant_users"
          using="merchantUserId,channelId"
        >
          <Typography className={styles.phoneNumbersTitle} variant="subtitle2">
            {translate(
              'resources.merchant_users.customFields.myFlinkitNumbers.label'
            )}
          </Typography>
          <ChannelAccordionsList />
        </ReferenceManyToManyField>
      </SimpleForm>
      <Toolbar>
        <DeleteWithConfirmButton
          confirmContent={translate('resources.merchant_users.confirmDeletion')}
          confirmTitle={record => [record.firstName, record.lastName].join(' ')}
          onConfirmDelete={softDelete}
        />
      </Toolbar>
    </ExtendedEdit>
  )
}

const MerchantUserEditAside = () => {
  const styles = useStyles()
  return (
    <div className={styles.merchantUserEditAside}>
      <MerchantUsersContactGroupsList />
      <MerchantUserChannelsList />
    </div>
  )
}

const MerchantUserChannelsList = () => {
  const notify = useNotify()
  const {record} = useEditContext()
  const canEdit = useHasPermission('edit', 'merchant_users')
  const {data: channels = [], refetch: refetchChannels} = useGetList<Channels>(
    'channels',
    {
      // @ts-ignore
      pagination: {},
      sort: {field: 'name', order: 'ASC'},
    },
    {enabled: !!record},
  )
  const {
    data: channelMerchantUsers = [],
    refetch: refetchChannelMerchantUsers,
  } = useGetList<ChannelMerchantUsers & {id: string}>(
    'channel_merchant_users',
    {
      filter: {merchantUserId: record?.id},
      // @ts-ignore
      pagination: {},
      sort: {field: 'channelId', order: 'ASC'},
    },
    {enabled: !!record},
  )
  const [
    insertChannelMerchantUser,
    {isLoading: isAddingChannelMerchantUser},
  ] = useCreate<ChannelMerchantUsers>()
  const insertChannelMerchantUserFromChannel = async (c: Channels) => {
    await insertChannelMerchantUser(
      'channel_merchant_users',
      {data: {channelId: c.id, merchantUserId: record?.id}},
      {returnPromise: true}
    )
    notify('resources.channel_merchant_users.action.messages.removed.channelAdded')
    await refetchChannelMerchantUsers()
  }
  const [createChannel, {isLoading: isCreatingTag}] = useCreate<Channels>()
  const onCreateChannel = async (name: string) => {
    const channels = await createChannel(
      'channels', {data: {name}}, {returnPromise: true},
    )
    refetchChannels()
    await insertChannelMerchantUserFromChannel(channels as Channels)
  }
  const [deleteChannelMerchantUser] = useMutation(
    CHANNEL_MERCHANT_USERS_DELETE_MUTATION,
    {
      onCompleted: () => {
        refetchChannelMerchantUsers()
        notify('resources.channel_merchant_users.action.messages.removed')
      },
    },
  )
  return (
    <MultipleChipSelect<
        Channels & {id: Identifier},
        ChannelMerchantUsers & {id: Identifier}
      >
      canCreateNewChoice
      canEditSelectedChoices={canEdit}
      choices={channels}
      isCreatingChoice={isCreatingTag || isAddingChannelMerchantUser}
      onCreateNewChoice={onCreateChannel}
      onDeleteChoice={channelMerchantUser => deleteChannelMerchantUser({
        variables: {
          channelId: channelMerchantUser.channelId,
          merchantUserId: record?.id,
        },
      })}
      onSelectChoice={insertChannelMerchantUserFromChannel}
      optionText={channelMerchantUser => channelMerchantUser?.name}
      selectedChoices={channelMerchantUsers}
      selectionToChoice={s => channels.find(t => t.id === s.channelId)!}
      title="Channels"
    />
  )
}

const MerchantUsersContactGroupsList = () => {
  const notify = useNotify()
  const translate = useTranslate()
  const {record} = useEditContext()
  const canEdit = useHasPermission('edit', 'merchant_users')
  const {data: contactGroups = []} = useGetList<ContactGroups>(
    'contact_groups',
    {
      // @ts-ignore
      pagination: {},
      sort: {field: 'name', order: 'ASC'},
    },
    {enabled: !!record},
  )
  const {
    data: merchantUserContactGroups = [],
    refetch: refetchMerchantUserContactGroups,
  } = useGetList<MerchantUserContactGroups & {id: string}>(
    'merchant_user_contact_groups',
    {
      filter: {merchantUserId: record?.id},
      // @ts-ignore
      pagination: {},
      sort: {field: 'contactGroupId', order: 'ASC'},
    },
    {enabled: !!record},
  )
  const [
    addMerchantUserToContactGroup,
    {isLoading: isAddingCustomerUserToContactGroup},
  ] = useCreate<MerchantUserContactGroups & {id: string}>()
  const onAddMerchantUserToContactGroup = async (c: ContactGroups) => {
    await addMerchantUserToContactGroup(
      'merchant_user_contact_groups',
      {data: {contactGroupId: c.id, merchantUserId: record?.id}},
      {returnPromise: true}
    )
    notify('resources.merchant_customer_users.successMessages.contactGroupAdded')
    refetchMerchantUserContactGroups()
  }
  const [deleteMerchantUserContactGroup] = useMutation(
    MERCHANT_USER_CONTACT_GROUP_DELETE_MUTATION,
    {
      onCompleted: () => {
        refetchMerchantUserContactGroups()
        notify('resources.merchant_customer_users.successMessages.contactGroupRemoved')
      },
    },
  )
  return (
    <MultipleChipSelect
      canEditSelectedChoices={canEdit}
      choices={Object.values(contactGroups)}
      isCreatingChoice={isAddingCustomerUserToContactGroup}
      onDeleteChoice={c => deleteMerchantUserContactGroup({
        variables: {
          contactGroupId: c.contactGroupId,
          merchantUserId: record?.id,
        },
      })}
      onSelectChoice={onAddMerchantUserToContactGroup}
      optionText={c => c?.name}
      selectedChoices={Object.values(merchantUserContactGroups)}
      selectionToChoice={s => contactGroups.find(c => c.id === s.contactGroupId)!}
      title={translate('resources.contactGroups.name')}
    />
  )
}

const MerchantUserProfileEdit: FC<{id: string}> = ({id}) => {
  const styles = useStyles()
  const translate = useTranslate()
  return (
    <ExtendedEdit
      id={id}
      redirect={false}
      resource="merchant_users"
      title={translate('resources.merchant_users.edit.title')}
    >
      <SimpleForm
        className={styles.merchantUserProfileEditRoot}
        resetOptions={{keepDirtyValues: true}}
        toolbar={(
          <AutoSave
            confirmationDuration={false}
            debounce={1000}
            typographyProps={{display: 'none'}}
          />
        )}
      >
        <TextInput source="firstName" validate={required()}/>
        <TextInput source="lastName" />
        <MerchantUserEmailEdit />
        <ReferenceManyToManyField
          reference="channels"
          sort={{field: "channel.whatsappAccount.phoneNumber", order: "DESC"}}
          through="channel_merchant_users"
          using="merchantUserId,channelId"
        >
          <Typography className={styles.phoneNumbersTitle} variant="subtitle2">
            {translate(
              'resources.merchant_users.customFields.myFlinkitNumbers.label'
            )}
          </Typography>
          <SingleFieldList display="grid" linkType={false}>
            <FunctionField<Channels> render={r =>
              <MuiTextField
                // Shrink the input label when the initial value of r is nullish.
                // See: https://github.com/mui/material-ui/issues/13013
                slotProps={{inputLabel: {shrink: !!r?.name}}}
                disabled
                label={(r?.name !== r?.whatsappAccount?.phoneNumber) ? r?.name : ''}
                size="small"
                value={r?.whatsappAccount?.phoneNumber}
                variant="filled"
              />
            }
            />
          </SingleFieldList>
        </ReferenceManyToManyField>
        <LanguageSwitcher />
        <Typography
          className={styles.emailNotificationPreferenceOptionsTitle}
          color="textPrimary"
          variant="subtitle2"
        >
          {translate('resources.merchant_users.emailNotification.title')}
        </Typography>
        <EmailNotificationPreferenceOptions />
      </SimpleForm>
    </ExtendedEdit>
  )
}

const MerchantUserEmailEdit = () => {
  const notify = useNotify()
  const styles = useStyles()
  const translate = useTranslate()
  const {record: {emailAddress: currentEmailAddress, id}, refetch} = useEditContext()
  const [updateMerchantUserEmailAddress, {loading}] = useMutation(
    UPDATE_MERCHANT_USER_EMAIL_ADDRESS_MUTATION,
    {
      onCompleted: () => notify('ra.notification.updated', {type: 'info'}),
      onError(e) {
        const errorCode = actionErrorCode(e)
        notify(
          `resources.merchant_users.errorMessages.${errorCode}`,
          {messageArgs: {_: translate('ra.message.error')}, type: 'error'},
        )
      },
    }
  )
  const save = useCallback(async ({emailAddress}: FieldValues) => {
    if (currentEmailAddress === emailAddress) return
    await updateMerchantUserEmailAddress({
      variables: {merchantUserId: id, newEmailAddress: emailAddress},
    })
    await refetch()
  }, [currentEmailAddress, id, updateMerchantUserEmailAddress, refetch])
  return (
    <Create
      className={styles.updateMerchantUserEmailAddressForm}
      redirect={false}
      resource="merchant_users"
    >
      <SimpleForm
        defaultValues={{emailAddress: currentEmailAddress}}
        toolbar={(
          <MerchantUserEmailAddressSaveButton
            currentEmailAddress={currentEmailAddress}
            disabled={loading}
            save={save}
          />
        )}
      >
        <Typography color="textPrimary" variant="subtitle2">
          {translate('dialogs.profile.merchantUsersSettings.updateEmailAddress.title')}
        </Typography>
        <TextInput
          onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
          parse={v => v?.toLowerCase()}
          source="emailAddress"
          validate={[
            regex(
              EMAIL_ADDRESS_REGEX,
              translate('resources.merchant_users.errorMessages.invalidEmailAddress'),
            ),
            required(),
          ]}
        />
      </SimpleForm>
    </Create>
  )
}

const MerchantUserEmailAddressSaveButton = ({
  currentEmailAddress = '', disabled = false, save,
}) => {
  const {emailAddress} = useWatch()
  return (
    <SaveContext.Provider value={{save}}>
      <SaveButton
        icon={<></>}
        disabled={disabled || (emailAddress === currentEmailAddress)}
        type="button"
        variant="outlined"
      />
    </SaveContext.Provider>
  )
}

const LanguageSwitcher = () => {
  const styles = useStyles()
  const locale = useLocale()
  const translate = useTranslate()
  return (
    <Box marginTop="1rem">
      <Typography color="textPrimary" variant="subtitle2">
        {translate('resources.merchant_users.fields.language')}
      </Typography>
      <SelectInput
        choices={
          sortBy(
            SUPPORTED_I18N_LANGUAGES
              .map(l => ({id: l, label: translate(`languages.${l}`)})),
            'label',
          )
        }
        className={styles.languageSwitcherSelectInput}
        defaultValue={locale}
        label={translate('resources.merchant_users.chooseLanguage')}
        optionText={({id: language, label}) => (
          <Box display="flex" justifyContent="space-between" width="100%">
            <Typography>{label}</Typography>
            {locale === language && (
              <Typography className={styles.checkMark}>✓</Typography>
            )}
          </Box>
        )}
        validate={required()}
        source="language"
        sx={{'& span[aria-hidden="true"]': {display: 'none'}}}
      />
    </Box>
  )
}

const EmailNotificationPreferenceOptions = () => {
  const styles = useStyles()
  const choices = [
    {
      description: useTranslate()(
        'resources.merchant_users.emailNotification.choices.reminders.description'
      ),
      id: 'REMINDERS',
      label: useTranslate()(
        'resources.merchant_users.emailNotification.choices.reminders.label'
      ),
    },
    {
      description: useTranslate()(
        'resources.merchant_users.emailNotification.choices.instant.description'
      ),
      id: 'INSTANT',
      label: useTranslate()(
        'resources.merchant_users.emailNotification.choices.instant.label'
      ),
    },
    {
      description: useTranslate()(
        'resources.merchant_users.emailNotification.choices.none.description'
      ),
      id: 'NONE',
      label: useTranslate()(
        'resources.merchant_users.emailNotification.choices.none.label'
      ),
    },
  ]
  return (
    <Paper className={styles.emailNotificationPreferenceOptionsPaper} elevation={2}>
      <RadioButtonGroupInput
        choices={choices}
        label={false}
        options={{className: styles.emailNotificationPreferenceOptions}}
        row={false}
        optionText={choice => (
          <>
            <Typography variant="body1">{choice.label}</Typography>
            <Typography color="textSecondary" variant="caption">
              {choice.description}
            </Typography>
          </>
        )}
        source="emailNotificationPreference"
      />
    </Paper>
  )
}

const UPDATE_MERCHANT_USER_EMAIL_ADDRESS_MUTATION = gql`
  mutation($newEmailAddress: String!, $merchantUserId: uuid){
    updateMerchantUserEmailAddress(
      merchantUserId: $merchantUserId
      newEmailAddress: $newEmailAddress
    )
  }
`

const RESTORE_DELETED_MERCHANT_USER_MUTATION = gql`
  mutation($emailAddress: String!, $firstName: String!, $lastName: String!){
    restoreMerchantUser(
      emailAddress: $emailAddress
      firstName: $firstName
      lastName: $lastName
    ){isRestored}
  }
`

const MERCHANT_USER_CONTACT_GROUP_DELETE_MUTATION = gql`
  mutation($contactGroupId: uuid!, $merchantUserId: uuid!){
    delete_merchant_user_contact_groups(where: {
      contactGroupId: {_eq: $contactGroupId}
      merchantUserId: {_eq: $merchantUserId},
    }){
      returning{contactGroupId merchantUserId}
    }
  }
`

const CHANNEL_MERCHANT_USERS_DELETE_MUTATION = gql`
  mutation($channelId: uuid!, $merchantUserId: uuid!){
    delete_channel_merchant_users(where: {
      channelId: {_eq: $channelId},
      merchantUserId: {_eq: $merchantUserId}
    }){returning{channelId merchantUserId}}
  }
`

const useStyles = makeStyles(theme => ({
  checkMark: {},
  contactFilterAll: {
    border: `1.5px solid ${theme.palette.info.main}`,
    color: theme.palette.info.main,
  },
  contactsVisibilityContainer: {
    marginBottom: theme.remSpacing(0.75),
    marginTop: theme.remSpacing(5),
  },
  editIsOwnerInput: {
    '& .MuiSwitch-root': {
      marginRight: theme.remSpacing(3),
    },
    margin: 0,
  },
  emailNotificationPreferenceOptions: {
    '& .Mui-checked': {
      color: theme.palette.info.main,
    },
    gap: theme.remSpacing(2),
  },
  emailNotificationPreferenceOptionsPaper: {
    '& .MuiFormControl-root': {
      width: '100%',
    },
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box',
    padding: `0 ${theme.remSpacing(2)}`,
    width: theme.remSpacing(48),
  },
  emailNotificationPreferenceOptionsTitle: {
    padding: `${theme.remSpacing(2)} 0`,
  },
  featureSettingsTitle: {
    marginBottom: theme.remSpacing(3.5),
  },
  languageSwitcherSelectInput: {
    // Hide the check mark on the input field itself so that its only displayed
    // on the popup list.
    '& $checkMark': {
      display: 'none',
    },
  },
  listDatagridChannelChipField: {
    backgroundColor: "#E3F1FA",
  },
  listDatagridContactGroupChipField: {
    backgroundColor: "#E5F6EA",
  },
  merchantUserCreateRoot: {
    '& .MuiCardContent-root': {
      paddingTop: 0,
    },
    '& > div': {
      marginTop: 0,
    },
    paddingLeft: 0,
    paddingRight: 0,
  },
  merchantUserEditAside: {
    boxSizing: 'border-box',
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.remSpacing(10),
      paddingRight: theme.remSpacing(10),
      width: '50%',
    },
  },
  merchantUserEditRoot: {
    '& .MuiStack-root > div:not(.MuiPaper-root)': {
      width: '100%',
    },
    marginBottom: theme.remSpacing(5),
  },
  merchantUserProfileEditRoot: {
    '& .MuiStack-root > div:not(.MuiPaper-root)': {
      width: '100%',
    },
  },
  phoneNumbersTitle: {
    margin: `${theme.remSpacing(2)} 0`,
  },
  updateMerchantUserEmailAddressForm: {
    '& .MuiCardContent-root': {
      [theme.breakpoints.down('lg')]: {
        paddingBottom: 0,
      },
    },
    minWidth: '100%',
    padding: 0,
  },
}))

export {
  MerchantUserCreate,
  MerchantUserEdit,
  MerchantUserList,
  MerchantUserProfileEdit,
}
